import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EditLineIcon } from '@/assets/icons/EditLineIcon';
import { Text } from '@/components/TextComponent';
import { Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { ProductConfigKeys, useJurisdictionStore } from '@/hooks/use-jurisdiction';

import { DEFAULT_QUICK_AMOUNTS } from './race-to-place-constants';

type QuickAmountsButtonsProps = {
    onEditPress: () => void;
    onAmountPress: (amount: number) => void;
    amount: number;
};

export const QuickAmountsButtons = memo(({ onEditPress, onAmountPress, amount }: QuickAmountsButtonsProps) => {
    const prismicConfiguredQuickAmounts = useJurisdictionStore(
        store => store.jurisdictionSettings?.productConfig?.keyValuePairs?.[ProductConfigKeys.RaceToPlaceAmounts]
    );
    const parsedPrismicQuickAmounts = prismicConfiguredQuickAmounts
        ? Object.keys(prismicConfiguredQuickAmounts).map(key => parseInt(prismicConfiguredQuickAmounts[key], 10))
        : DEFAULT_QUICK_AMOUNTS;

    const buttons = useMemo(() => [...parsedPrismicQuickAmounts, 'edit'], [parsedPrismicQuickAmounts]);

    const handlePress = useCallback(
        (button: string | number) => {
            if (typeof button === 'string') {
                onEditPress();
            } else {
                if (button === amount) {
                    onAmountPress(0);
                    return;
                }
                onAmountPress(button);
            }
        },
        [onEditPress, amount, onAmountPress]
    );

    const hasCustomAmount = amount !== 0 && !buttons.find(button => button === amount);
    const { t } = useTranslation(['common']);

    return (
        <Row gap={'s8'}>
            {buttons.map(button => {
                const isQuickAmountSelected = typeof button === 'number' && button === amount;
                const isEditSelected = hasCustomAmount && button === 'edit';
                const isSelected = isQuickAmountSelected || isEditSelected;

                return (
                    <TouchableBox
                        borderRadius={'r10'}
                        key={button}
                        backgroundColor={
                            isSelected
                                ? ds.colorToken.background.neutral.inverse
                                : ds.colorToken.background.neutral.subtle
                        }
                        py={'s8'}
                        px={'s8'}
                        alignItems={'center'}
                        flex={1}
                        onPress={() => handlePress(button)}
                        testID={`quick-amount-button-${button}`}
                    >
                        {button === 'edit' ? (
                            <Row gap="s4" alignItems={'center'} px={hasCustomAmount ? 's8' : 's0'}>
                                <Text
                                    variant={'titleSmall'}
                                    color={isSelected ? 'neutral900' : ds.colorToken.text.primary}
                                    ellipsizeMode={'tail'}
                                    numberOfLines={1}
                                    letterSpacing={-0.08}
                                >
                                    {hasCustomAmount ? `$${amount}` : t('custom')}
                                </Text>
                                {hasCustomAmount ? <EditLineIcon /> : null}
                            </Row>
                        ) : (
                            <Text
                                variant={'titleSmall'}
                                color={isQuickAmountSelected ? 'neutral900' : ds.colorToken.text.primary}
                                letterSpacing={-0.08}
                            >{`$${button}`}</Text>
                        )}
                    </TouchableBox>
                );
            })}
        </Row>
    );
});
